import React from "react";
import TradeMarketExecutionContent from "./TradeMarketExecutionContent";
import TradeLimitStopContent from "./TradeLimitStopContent";
import TradeStopLimitContent from "./TradeStopLimitContent";
import TradeModifyContent from "./TradeModifyContent";
import TradeCloseByContent from "./TradeCloseByContent";
import type { SlTpSymbolInfo } from "../lib/slTpMoney";

interface OrderTypeContentMapperProps {
  orderType: string;
  type?: "new" | "modify" | "closeBy";
  currentSymbol: string;
  bidPrice: number;
  askPrice: number;
  formatPrice: (price: number) => string;
  price: number | null;
  priceInput: string;
  stopLimitPrice: number | null;
  stopLimitPriceInput: string;
  stopLoss: number | null;
  stopLossInput: string;
  takeProfit: number | null;
  takeProfitInput: string;
  onPriceChange: (change: number) => void;
  onPriceInputChange: (value: string) => void;
  onStopLimitPriceChange: (change: number) => void;
  onStopLimitPriceInputChange: (value: string) => void;
  onStopLossChange: (change: number) => void;
  onStopLossInputChange: (value: string) => void;
  onTakeProfitChange: (change: number) => void;
  onTakeProfitInputChange: (value: string) => void;
  onSellByMarket: () => void;
  onBuyByMarket: () => void;
  onPlaceOrder: () => void;
  onSaveChanges?: () => void;
  isTrading: boolean;
  errorMessage: string;
  isDesktop?: boolean;
  /** Lot size + tick-value info for money⇄price SL/TP conversion (proposal 18). */
  volume?: number;
  digits?: number;
  symbolInfo?: SlTpSymbolInfo | null;
}

export default function OrderTypeContentMapper({
  orderType,
  type,
  currentSymbol,
  bidPrice,
  askPrice,
  formatPrice,
  price,
  priceInput,
  stopLimitPrice,
  stopLimitPriceInput,
  stopLoss,
  stopLossInput,
  takeProfit,
  takeProfitInput,
  onPriceChange,
  onPriceInputChange,
  onStopLimitPriceChange,
  onStopLimitPriceInputChange,
  onStopLossChange,
  onStopLossInputChange,
  onTakeProfitChange,
  onTakeProfitInputChange,
  onSellByMarket,
  onBuyByMarket,
  onPlaceOrder,
  onSaveChanges,
  isTrading,
  errorMessage,
  isDesktop = false,
  volume = 0.01,
  digits = 5,
  symbolInfo = null,
}: OrderTypeContentMapperProps) {
  // Market Execution
  if (orderType === "Market Execution") {
    return (
      <TradeMarketExecutionContent
        bidPrice={bidPrice}
        askPrice={askPrice}
        formatPrice={formatPrice}
        stopLoss={stopLoss}
        stopLossInput={stopLossInput}
        takeProfit={takeProfit}
        takeProfitInput={takeProfitInput}
        onStopLossChange={onStopLossChange}
        onStopLossInputChange={onStopLossInputChange}
        onTakeProfitChange={onTakeProfitChange}
        onTakeProfitInputChange={onTakeProfitInputChange}
        onSellByMarket={onSellByMarket}
        onBuyByMarket={onBuyByMarket}
        isTrading={isTrading}
        isDesktop={isDesktop}
        volume={volume}
        digits={digits}
        symbolInfo={symbolInfo}
      />
    );
  }

  // Buy/Sell Limit and Buy/Sell Stop
  if (
    orderType === "Buy Limit" ||
    orderType === "Sell Limit" ||
    orderType === "Buy Stop" ||
    orderType === "Sell Stop"
  ) {
    return (
      <TradeLimitStopContent
        orderType={orderType}
        type={type}
        bidPrice={bidPrice}
        askPrice={askPrice}
        formatPrice={formatPrice}
        price={price}
        priceInput={priceInput}
        stopLoss={stopLoss}
        stopLossInput={stopLossInput}
        takeProfit={takeProfit}
        takeProfitInput={takeProfitInput}
        onPriceChange={onPriceChange}
        onPriceInputChange={onPriceInputChange}
        onStopLossChange={onStopLossChange}
        onStopLossInputChange={onStopLossInputChange}
        onTakeProfitChange={onTakeProfitChange}
        onTakeProfitInputChange={onTakeProfitInputChange}
        onPlaceOrder={onPlaceOrder}
        isTrading={isTrading}
        isDesktop={isDesktop}
      />
    );
  }

  // Buy/Sell Stop Limit
  if (
    orderType === "Buy Stop Limit" ||
    orderType === "Sell Stop Limit"
  ) {
    return (
      <TradeStopLimitContent
        orderType={orderType}
        type={type}
        bidPrice={bidPrice}
        askPrice={askPrice}
        formatPrice={formatPrice}
        price={price}
        priceInput={priceInput}
        stopLimitPrice={stopLimitPrice}
        stopLimitPriceInput={stopLimitPriceInput}
        stopLoss={stopLoss}
        stopLossInput={stopLossInput}
        takeProfit={takeProfit}
        takeProfitInput={takeProfitInput}
        onPriceChange={onPriceChange}
        onPriceInputChange={onPriceInputChange}
        onStopLimitPriceChange={onStopLimitPriceChange}
        onStopLimitPriceInputChange={onStopLimitPriceInputChange}
        onStopLossChange={onStopLossChange}
        onStopLossInputChange={onStopLossInputChange}
        onTakeProfitChange={onTakeProfitChange}
        onTakeProfitInputChange={onTakeProfitInputChange}
        onPlaceOrder={onPlaceOrder}
        isTrading={isTrading}
      />
    );
  }

  // Modify
  if (orderType === "Modify") {
    return (
      <TradeModifyContent
        stopLoss={stopLoss}
        stopLossInput={stopLossInput}
        takeProfit={takeProfit}
        takeProfitInput={takeProfitInput}
        onStopLossChange={onStopLossChange}
        onStopLossInputChange={onStopLossInputChange}
        onTakeProfitChange={onTakeProfitChange}
        onTakeProfitInputChange={onTakeProfitInputChange}
        onSaveChanges={onSaveChanges}
        isTrading={isTrading}
        digits={digits}
      />
    );
  }

  // Close By
  if (orderType === "Close By") {
    return (
      <TradeCloseByContent />
    );
  }

  // Default case or unsupported order types
  return null;
}
